'use client';

import { useMemo } from 'react';
import { XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Area, AreaChart, Legend } from 'recharts';
import { TrendingUp, TrendingDown, Minus, Split } from 'lucide-react';
import { format } from 'date-fns';

import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface StepProgressionDataPoint {
    period: string;
    periodLabel: string;
    sourceVisitors: number;
    targetVisitors: number;
    progressionRate: number;
    dropOff: number;
    dropOffRate: number;
}

interface VariantProgressionDataPoint extends StepProgressionDataPoint {
    variantId: string;
    variantLabel: string | null;
}

interface StepProgressionSummary {
    totalSourceVisitors: number;
    totalTargetVisitors: number;
    overallProgressionRate: number;
    overallDropOffRate: number;
    avgDailySourceVisitors: number;
    avgDailyProgressionRate: number;
    minProgressionRate: number;
    maxProgressionRate: number;
    trendDirection: 'up' | 'down' | 'stable';
}

interface VariantSummary extends StepProgressionSummary {
    variantId: string;
    variantLabel: string | null;
}

interface VariantInfo {
    variantId: string;
    variantLabel: string | null;
}

interface StepProgressionChartProps {
    data?: {
        dataPoints: StepProgressionDataPoint[];
        summary: StepProgressionSummary;
        isSplitTest?: boolean;
        variants?: VariantInfo[];
        variantDataPoints?: Record<string, VariantProgressionDataPoint[]>;
        variantSummaries?: Record<string, VariantSummary>;
    };
    sourceStepName: string;
    targetStepName: string;
    isLoading?: boolean;
    className?: string;
    /** If true, show variant comparison when data has variants */
    showVariantComparison?: boolean;
}

// Colors for variants (distinct, colorblind-friendly)
const VARIANT_COLORS = [
    { stroke: 'hsl(262, 83%, 58%)', fill: 'hsl(262, 83%, 58%)', name: 'purple' },  // Purple
    { stroke: 'hsl(199, 89%, 48%)', fill: 'hsl(199, 89%, 48%)', name: 'blue' },    // Blue
    { stroke: 'hsl(142, 71%, 45%)', fill: 'hsl(142, 71%, 45%)', name: 'green' },   // Green
    { stroke: 'hsl(25, 95%, 53%)', fill: 'hsl(25, 95%, 53%)', name: 'orange' },    // Orange
];

/**
 * Step-to-Step Progression Chart with A/B Test Variant Comparison
 * Shows progression rate over time with summary statistics
 * When the source step is a split test, shows multiple curves for comparison
 */
export function StepProgressionChart({
    data,
    sourceStepName,
    targetStepName,
    isLoading,
    className,
    showVariantComparison = true,
}: StepProgressionChartProps) {
    const { t } = useTranslation('storefront');
    // Check if we should show variant comparison
    const hasVariants = data?.isSplitTest && data?.variants && data.variants.length > 0;
    const shouldShowVariants = showVariantComparison && hasVariants && data?.variantDataPoints;

    // Transform data for chart - either single line or multi-variant
    const chartData = useMemo(() => {
        if (!data?.dataPoints) return [];

        if (shouldShowVariants && data.variantDataPoints) {
            // Merge variant data points into a single array by period
            const periodMap: Record<string, any> = {};
            
            // First, get all periods from the overall data
            for (const point of data.dataPoints) {
                periodMap[point.period] = {
                    date: point.period,
                    label: point.periodLabel,
                    overall: point.progressionRate,
                    overallSource: point.sourceVisitors,
                    overallTarget: point.targetVisitors,
                };
            }

            // Then add variant-specific rates
            for (const [variantId, points] of Object.entries(data.variantDataPoints)) {
                for (const point of points) {
                    if (!periodMap[point.period]) {
                        periodMap[point.period] = {
                            date: point.period,
                            label: point.periodLabel,
                        };
                    }
                    periodMap[point.period][`rate_${variantId}`] = point.progressionRate;
                    periodMap[point.period][`source_${variantId}`] = point.sourceVisitors;
                    periodMap[point.period][`target_${variantId}`] = point.targetVisitors;
                }
            }

            return Object.values(periodMap).sort((a, b) => 
                new Date(a.date).getTime() - new Date(b.date).getTime()
            );
        }

        // Single line mode
        return data.dataPoints.map((point) => ({
            date: point.period,
            label: point.periodLabel,
            rate: point.progressionRate,
            sourceVisitors: point.sourceVisitors,
            targetVisitors: point.targetVisitors,
            dropOff: point.dropOffRate,
        }));
    }, [data, shouldShowVariants]);

    const hasData = chartData.length > 0;
    const summary = data?.summary;
    const variantSummaries = data?.variantSummaries;
    const variants = data?.variants || [];

    // Format date for tooltip
    const formatDateStr = (dateStr: string) => {
        try {
            return format(new Date(dateStr), 'MMM d');
        } catch {
            return dateStr;
        }
    };

    // Get variant display name
    const getVariantName = (variantId: string) => {
        const variant = variants.find(v => v.variantId === variantId);
        return variant?.variantLabel || variantId.split('_').pop() || variantId;
    };

    if (isLoading) {
        return (
            <div className={cn("space-y-3", className)}>
                <div className="flex items-center justify-between">
                    <Skeleton className="h-6 w-20" />
                    <Skeleton className="h-4 w-24" />
                </div>
                <Skeleton className="h-[150px] w-full rounded-md" />
            </div>
        );
    }

    if (!hasData) {
        return (
            <div className={cn("h-[150px] flex items-center justify-center text-muted-foreground text-xs", className)}>
                {t('analytics.noDataForPeriod')}
            </div>
        );
    }

    return (
        <div className={cn("space-y-2.5", className)}>
            {/* Summary Row */}
            {summary && (
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        {shouldShowVariants && (
                            <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-medium gap-0.5 bg-purple-500/10 text-purple-600 border-purple-500/20">
                                <Split className="h-2.5 w-2.5" />
                                A/B Test
                            </Badge>
                        )}
                        <span className="text-xl font-bold tabular-nums">{summary.overallProgressionRate.toFixed(1)}%</span>
                        <Badge 
                            variant="secondary"
                            className={cn(
                                "h-5 px-1.5 text-[10px] font-medium gap-0.5",
                                summary.trendDirection === 'up' && 'bg-green-500/10 text-green-600 border-green-500/20',
                                summary.trendDirection === 'down' && 'bg-red-500/10 text-red-600 border-red-500/20'
                            )}
                        >
                            {summary.trendDirection === 'up' && <TrendingUp className="h-2.5 w-2.5" />}
                            {summary.trendDirection === 'down' && <TrendingDown className="h-2.5 w-2.5" />}
                            {summary.trendDirection === 'stable' && <Minus className="h-2.5 w-2.5" />}
                            {summary.trendDirection}
                        </Badge>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground tabular-nums">
                        <span className="font-medium">{summary.totalSourceVisitors.toLocaleString()}</span>
                        <span>→</span>
                        <span className="font-medium">{summary.totalTargetVisitors.toLocaleString()}</span>
                    </div>
                </div>
            )}

            {/* Variant Summaries (when showing variant comparison) */}
            {shouldShowVariants && variantSummaries && (
                <div className="flex flex-wrap gap-2">
                    {variants.map((variant, idx) => {
                        const variantSummary = variantSummaries[variant.variantId];
                        if (!variantSummary) return null;
                        const color = VARIANT_COLORS[idx % VARIANT_COLORS.length];
                        return (
                            <div 
                                key={variant.variantId} 
                                className="flex items-center gap-1.5 px-2 py-1 rounded bg-muted/50 text-xs"
                            >
                                <div 
                                    className="w-2.5 h-2.5 rounded-full" 
                                    style={{ backgroundColor: color.stroke }}
                                />
                                <span className="font-medium">{getVariantName(variant.variantId)}:</span>
                                <span className="tabular-nums font-bold">{variantSummary.overallProgressionRate.toFixed(1)}%</span>
                                <span className="text-muted-foreground text-[10px]">
                                    ({variantSummary.totalSourceVisitors}→{variantSummary.totalTargetVisitors})
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}
            
            {/* Chart */}
            <div className="h-[135px]">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                        <defs>
                            <linearGradient id="progressionGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                            </linearGradient>
                            {variants.map((variant, idx) => (
                                <linearGradient 
                                    key={variant.variantId} 
                                    id={`gradient_${variant.variantId}`} 
                                    x1="0" y1="0" x2="0" y2="1"
                                >
                                    <stop offset="5%" stopColor={VARIANT_COLORS[idx % VARIANT_COLORS.length].fill} stopOpacity={0.3} />
                                    <stop offset="95%" stopColor={VARIANT_COLORS[idx % VARIANT_COLORS.length].fill} stopOpacity={0} />
                                </linearGradient>
                            ))}
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted/50" vertical={false} />
                        <XAxis 
                            dataKey="label" 
                            tick={{ fontSize: 10 }}
                            tickLine={false}
                            axisLine={false}
                            interval="preserveStartEnd"
                        />
                        <YAxis 
                            domain={[0, 100]}
                            tick={{ fontSize: 10 }}
                            tickLine={false}
                            axisLine={false}
                            tickFormatter={(value) => `${value}%`}
                            width={35}
                        />
                        <Tooltip
                            content={({ active, payload }) => {
                                if (!active || !payload?.length) return null;
                                const d = payload[0].payload;
                                return (
                                    <div className="bg-popover border rounded-md shadow-md px-2.5 py-2 text-xs">
                                        <p className="font-medium text-foreground mb-1">{formatDateStr(d.date)}</p>
                                        {shouldShowVariants ? (
                                            // Multi-variant tooltip
                                            <div className="space-y-1">
                                                {variants.map((variant, idx) => {
                                                    const rate = d[`rate_${variant.variantId}`];
                                                    const source = d[`source_${variant.variantId}`];
                                                    const target = d[`target_${variant.variantId}`];
                                                    if (rate === undefined) return null;
                                                    const color = VARIANT_COLORS[idx % VARIANT_COLORS.length];
                                                    return (
                                                        <div key={variant.variantId} className="flex items-center gap-2">
                                                            <div 
                                                                className="w-2 h-2 rounded-full flex-shrink-0" 
                                                                style={{ backgroundColor: color.stroke }}
                                                            />
                                                            <span className="font-medium">{getVariantName(variant.variantId)}:</span>
                                                            <span className="tabular-nums font-bold">{rate.toFixed(1)}%</span>
                                                            <span className="text-muted-foreground text-[10px]">
                                                                ({target}/{source})
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            // Single line tooltip
                                            <>
                                                <p className="text-primary font-bold text-sm tabular-nums">{d.rate.toFixed(1)}%</p>
                                                <p className="text-muted-foreground text-[10px] tabular-nums">
                                                    {d.targetVisitors} / {d.sourceVisitors} visitors
                                                </p>
                                            </>
                                        )}
                                    </div>
                                );
                            }}
                        />
                        {shouldShowVariants ? (
                            // Multiple areas for variants
                            variants.map((variant, idx) => (
                                <Area
                                    key={variant.variantId}
                                    type="monotone"
                                    dataKey={`rate_${variant.variantId}`}
                                    stroke={VARIANT_COLORS[idx % VARIANT_COLORS.length].stroke}
                                    fill={`url(#gradient_${variant.variantId})`}
                                    strokeWidth={2}
                                    name={getVariantName(variant.variantId)}
                                />
                            ))
                        ) : (
                            // Single area
                            <Area
                                type="monotone"
                                dataKey="rate"
                                stroke="hsl(var(--primary))"
                                fill="url(#progressionGradient)"
                                strokeWidth={2}
                            />
                        )}
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
